import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { type Message } from "shared/apiClient";
import { type QuestionData } from "@/components/wizard/lib";

import { createMessageEvent, useChannel } from "shared/channel";
import { useAccount, useCaptureEvent, CaptureEventType } from "@/hooks";

import { Button, Slider, Toggle } from "shared/components";
import { QuestionLoader } from "@/components/wizard/questionLoader/QuestionLoader";
import { Background } from "./Background";
import { Person } from "./Person";
import { TreeLarge } from "./TreeLarge";
import { TreeMedium } from "./TreeMedium";
import { TreeSmall } from "./TreeSmall";

export interface TreeSizeStepProps {
  disabled?: boolean;
  message: Message;
  showLoading?: boolean;
}

export function TreeSizeStep({
  disabled,
  message,
  showLoading,
}: TreeSizeStepProps) {
  const { captureEvent } = useCaptureEvent();

  const questionData = message.question_data as QuestionData;

  const { channel } = useChannel();

  const { conversation } = useAccount();

  const [submitting, setSubmitting] = useState(false);
  const [treeDiameter, setTreeDiameter] = useState(0.3);
  const [presetSize, setPresetSize] = useState<
    "small" | "medium" | "large" | null
  >(null);

  const handleClickNext = () => {
    if (channel && conversation?.id) {
      setSubmitting(true);

      const text = `The tree will have a trunk diameter at breast height (DBH) of ${treeDiameter.toFixed(1)} meters.`;

      window.localStorage.setItem(
        `${message.conversation_id}.treeDiameter`,
        `${treeDiameter}`,
      );

      const messageDescriptor = createMessageEvent({
        conversation_id: conversation.id,
        text,
      });

      console.log("<TreeSizeStep> handleClickNext()", messageDescriptor);
      channel.sendMessage(messageDescriptor);

      captureEvent(CaptureEventType.USER_ANSWERED_QUESTION, {
        messageId: message.id,
      });
    }
  };

  const lineWidth = useMemo(() => {
    if (treeDiameter <= 0.4) {
      return treeDiameter * 35;
    }
    if (treeDiameter > 0.4 && treeDiameter <= 0.9) {
      return treeDiameter * 60;
    }
    if (treeDiameter > 0.9) {
      return treeDiameter * 70;
    }
  }, [treeDiameter]);

  const treeMarginLeft = useMemo(() => {
    if (treeDiameter === 0.1) {
      return "mr-[-15px]";
    }
    if (treeDiameter === 0.2) {
      return "mr-[-18px]";
    }
    if (treeDiameter === 0.3) {
      return "mr-[-22px]";
    }
    if (treeDiameter === 0.4) {
      return "mr-[-25px]";
    }
    if (treeDiameter === 0.5) {
      return "mr-[-10px]";
    }
    if (treeDiameter === 0.6) {
      return "mr-[-12px]";
    }
    if (treeDiameter === 0.7) {
      return "mr-[-13px]";
    }
    if (treeDiameter === 0.8) {
      return "mr-[-15px]";
    }
    if (treeDiameter === 0.9) {
      return "mr-[-18px]";
    }
    if (treeDiameter > 0.9) {
      return "";
    }
  }, [treeDiameter]);

  const treeMarginRight = useMemo(() => {
    if (treeDiameter < 0.9) {
      return "";
    }
    if (treeDiameter === 1.0) {
      return "ml-[-55px]";
    }
    if (treeDiameter === 1.1) {
      return "ml-[-58px]";
    }
    if (treeDiameter === 1.2) {
      return "ml-[-60px]";
    }
  }, [treeDiameter]);

  const treeSize = (treeDiameter + 0.2) * 550;

  const renderDiagram = () => {
    return (
      <>
        <Background className="w-full grow" />

        <div className="absolute bottom-0 left-0 right-0 top-0 z-10 flex items-end justify-start">
          <div className="grow-[2]" />
          <Person className="mb-6" style={{ transform: "scale(1.1, 1.1)" }} />
          <div className="grow-[8]" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 top-0 z-10 flex flex-col items-center">
          {treeDiameter <= 0.4 && (
            <TreeSmall
              className={`absolute bottom-[19px] ${treeMarginLeft}`}
              style={{
                width: `${treeSize}px`,
                height: `${treeSize}px`,
              }}
            />
          )}
          {treeDiameter > 0.4 && treeDiameter <= 0.9 && (
            <TreeMedium
              className={`absolute bottom-[19px] ${treeMarginLeft}`}
              style={{
                width: `${treeSize}px`,
                height: `${treeSize}px`,
              }}
            />
          )}
          {treeDiameter > 0.9 && (
            <TreeLarge
              className={`absolute bottom-[19px] ${treeMarginRight}`}
              style={{
                width: `${treeSize}px`,
                height: `${treeSize}px`,
              }}
            />
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 top-0 z-50 flex flex-col items-center">
          <div className="absolute bottom-[64px] flex flex-col items-center gap-2">
            <div className="flex items-center">
              <div className="bg-textPrimary h-2 w-[1px]" />
              <div
                className="border-textPrimary h-[1px] border-t border-dashed"
                style={{ width: `${lineWidth}px` }}
              />
              <div className="bg-textPrimary h-2 w-[1px]" />
            </div>
            <div className="relative flex h-[1px] w-[1px] justify-center">
              <p className="bg-interfaceWhite text-textPrimary absolute rounded px-1 text-bodymd">
                {treeDiameter.toFixed(1)}m
              </p>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderDefaultButtons = () => {
    return (
      <>
        <Toggle
          disabled={submitting || showLoading || disabled}
          onClick={() => {
            setPresetSize("small");
            setTreeDiameter(0.3);
          }}
          pressed={presetSize === "small"}
          variant="rounded"
        >
          Small
        </Toggle>
        <Toggle
          disabled={submitting || showLoading || disabled}
          onClick={() => {
            setPresetSize("medium");
            setTreeDiameter(0.6);
          }}
          pressed={presetSize === "medium"}
          variant="rounded"
        >
          Medium
        </Toggle>
        <Toggle
          disabled={submitting || showLoading || disabled}
          onClick={() => {
            setPresetSize("large");
            setTreeDiameter(1);
          }}
          pressed={presetSize === "large"}
          variant="rounded"
        >
          Large
        </Toggle>
      </>
    );
  };

  const renderFooter = () => {
    return (
      <>
        <Button
          className="wizard-step-hide"
          data-testid="next-button"
          disabled={submitting || showLoading || disabled}
          onClick={handleClickNext}
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {questionData.next_button_label || "Next"}
        </Button>
      </>
    );
  };

  if (showLoading) {
    return <QuestionLoader className="" />;
  }

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-4 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-tree-size"
    >
      <div className="relative z-0 flex min-h-[460px] w-full flex-col overflow-hidden">
        {renderDiagram()}
      </div>

      <div className="w-full">
        <h2 className="text-textPrimary text-headingxl">
          How large is the tree?
        </h2>
      </div>

      <div className="border-blueDefault flex flex-col gap-4 rounded-lg border px-4 py-5">
        <div className="flex gap-2">{renderDefaultButtons()}</div>

        <div className="flex flex-col">
          <label className="text-textPrimary text-bodymd pb-2 font-semibold">
            Tree diameter at breast height
          </label>
          <div className="flex items-center gap-2">
            <Slider
              defaultValue={[0.3]}
              disabled={submitting || showLoading || disabled}
              max={1.0}
              min={0.1}
              step={0.1}
              onValueChange={(value) => {
                setTreeDiameter(value[0]);
                setPresetSize(null);
              }}
              value={[treeDiameter]}
            />
            {treeDiameter < 1.0 ? (
              <div className="w-[20px]">{treeDiameter}m</div>
            ) : null}
            {treeDiameter >= 1.0 ? (
              <div className="w-[20px]">{treeDiameter}m+</div>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex justify-end">{renderFooter()}</div>
      </div>
    </div>
  );
}
